// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import AbilityGroupAbility from "./ability-group-ability.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "name": {
      "column": {
        "default": null,
        "name": "name",
        "null": false,
        "type": "string"
      },
      "name": "name",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "AbilityGroup",
  "collectionKey": "ability_groups",
  "collectionName": "ability_groups",
  "i18nKey": "ability_group",
  "camelizedLower": "abilityGroup",
  "name": "AbilityGroup",
  "nameDasherized": "ability-group",
  "pluralName": "ability_groups",
  "ransackable_associations": [
    {
      "className": "Ability",
      "collectionName": "abilities",
      "foreignKey": "ability_id",
      "name": "abilities",
      "macro": "has_many",
      "resource_name": "Ability",
      "through": "ability_group_abilities"
    },
    {
      "className": "AbilityGroupAbility",
      "collectionName": "ability_group_abilities",
      "foreignKey": "ability_group_id",
      "name": "ability_group_abilities",
      "macro": "has_many",
      "resource_name": "AbilityGroupAbility",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "RoleAbilityGroup",
      "collectionName": "role_ability_groups",
      "foreignKey": "ability_group_id",
      "name": "role_ability_groups",
      "macro": "has_many",
      "resource_name": "RoleAbilityGroup",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "name",
      "column": {
        "default": null,
        "name": "name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "AbilityGroupAbility",
      "collectionName": "ability_group_abilities",
      "foreignKey": "ability_group_id",
      "name": "ability_group_abilities",
      "macro": "has_many",
      "resource_name": "AbilityGroupAbility",
      "through": null
    }
  ],
  "paramKey": "ability_group",
  "primaryKey": "id"
}

/** Frontend model for AbilityGroup. */
export default class AbilityGroup extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./ability-group-ability.js").default>} */
  abilityGroupAbilities() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["ability_group_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "ability_group_abilities",
        model: this,
        modelClass: AbilityGroupAbility
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./ability-group-ability.js").default>>} */
  loadAbilityGroupAbilities() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["ability_group_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "ability_group_abilities",
        model: this,
        modelClass: AbilityGroupAbility
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./ability-group-ability.js").default>>} */
  ensureAbilityGroupAbilitiesLoaded() {
    return this.ensureAssociationLoaded("ability_group_abilities")
  }
}
